import { useCallback, useEffect, useMemo, useState } from 'react';
import { flattenLeafCategories } from '@/tiktok-platform/utils/tiktokCategoryTree';
import {
  isOfficialTemplateScope,
  isPersonalTemplateScope,
  isTeamTemplateScope,
  TIKTOK_TEMPLATE_SCOPES,
} from '@/tiktok-platform/constants/tiktokTemplateScope';
import { isTiktokShopAuthorized } from '@/tiktok-platform/constants/tiktokRoutes';
import { useTiktokShop } from '@/tiktok-platform/context/TiktokShopContext';
import { tiktokPlatformAPI, fundingAuthAPI, isRequestAborted, getIpeWorkspaceMode } from '@/utils/api';
import { isTeamSpaceEntryAvailable } from '@/pages/ipe/workbench/utils/accountCenterUtils';
import {
  isAllShopsScope,
  isConcreteShopId,
} from '@/utils/platformShopScope';
import {
  invalidateAccountCenterCache,
  loadCanAccessTeamCached,
  loadShopCategoriesCached,
  peekCanAccessTeam,
  peekShopCategories,
} from '@/tiktok-platform/utils/tiktokTemplateScopeCache';

/**
 * 模板库：单店铺独享按当前店铺 ID；团队店铺共享按当前店类目在工作区可见。
 *
 * 对齐 SHEIN 进页体感：
 * - 默认 deferCategories：进页不抢拉 TTS 大类目树，列表先出；
 * - 有缓存则秒开筛选项；打开类目筛选 / idle 时再拉取。
 *
 * @param {{
 *   loadCategories?: boolean,
 *   deferCategories?: boolean,
 *   preferredShopId?: string,
 * }} [options]
 */
export default function useTiktokTemplateScope({
  loadCategories = true,
  deferCategories = true,
  preferredShopId,
} = {}) {
  const { activeShop, activeShopId, shops, scopedShops } = useTiktokShop();

  const authShopId = useMemo(() => {
    if (isConcreteShopId(preferredShopId)) return String(preferredShopId);
    if (activeShop?.id != null && isConcreteShopId(activeShop.id)) return String(activeShop.id);
    if (isConcreteShopId(activeShopId)) return String(activeShopId);
    const pool = (Array.isArray(scopedShops) && scopedShops.length
      ? scopedShops
      : (shops || []).filter((s) => isTiktokShopAuthorized(s)));
    const first = pool.find((s) => s?.id != null);
    return first?.id != null ? String(first.id) : '';
  }, [preferredShopId, activeShop, activeShopId, shops, scopedShops]);

  const listAllShops = preferredShopId != null && String(preferredShopId) !== ''
    ? isAllShopsScope(preferredShopId)
    : isAllShopsScope(activeShopId);

  const seedShopId = (() => {
    if (isConcreteShopId(preferredShopId)) return String(preferredShopId);
    if (isConcreteShopId(activeShopId)) return String(activeShopId);
    return '';
  })();

  const [scopeTab, setScopeTab] = useState(TIKTOK_TEMPLATE_SCOPES.personal);
  const [workspaceMode, setWorkspaceMode] = useState(() => getIpeWorkspaceMode());
  const [categories, setCategories] = useState(() => {
    if (!loadCategories || !seedShopId) return [];
    return peekShopCategories(seedShopId) || [];
  });
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  /** 有缓存则立刻启用；否则 defer 时等 ensureCategoriesLoaded / idle */
  const [categoriesEnabled, setCategoriesEnabled] = useState(() => {
    if (!loadCategories) return false;
    if (seedShopId && peekShopCategories(seedShopId) != null) return true;
    return !deferCategories;
  });
  const [canAccessTeam, setCanAccessTeam] = useState(() => {
    const cached = peekCanAccessTeam(getIpeWorkspaceMode());
    return cached == null ? false : cached;
  });

  const ensureCategoriesLoaded = useCallback(() => {
    if (!loadCategories) return;
    setCategoriesEnabled(true);
  }, [loadCategories]);

  useEffect(() => {
    const sync = (ev) => {
      const next = ev?.detail?.mode;
      if (next === 'personal' || next === 'team') {
        setWorkspaceMode(next);
        return;
      }
      setWorkspaceMode(getIpeWorkspaceMode());
    };
    const onInvalidate = () => {
      invalidateAccountCenterCache();
      setWorkspaceMode(getIpeWorkspaceMode());
    };
    window.addEventListener('ipe:workspace-mode-changed', sync);
    window.addEventListener('ipe-workspace-mode-changed', sync);
    window.addEventListener('ipe:workspace-refresh', onInvalidate);
    return () => {
      window.removeEventListener('ipe:workspace-mode-changed', sync);
      window.removeEventListener('ipe-workspace-mode-changed', sync);
      window.removeEventListener('ipe:workspace-refresh', onInvalidate);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const cached = peekCanAccessTeam(workspaceMode);
    if (cached != null) setCanAccessTeam(cached);
    loadCanAccessTeamCached(workspaceMode, async () => {
      const center = await fundingAuthAPI.getAccountCenter();
      return isTeamSpaceEntryAvailable(center);
    })
      .then((ok) => {
        if (!cancelled) setCanAccessTeam(Boolean(ok));
      })
      .catch((err) => {
        if (!cancelled && !isRequestAborted(err)) setCanAccessTeam(false);
      });
    return () => { cancelled = true; };
  }, [workspaceMode]);

  useEffect(() => {
    if (!canAccessTeam && isTeamTemplateScope(scopeTab)) {
      setScopeTab(TIKTOK_TEMPLATE_SCOPES.personal);
    }
  }, [canAccessTeam, scopeTab]);

  // 店铺切换：有缓存秒开，否则回到 defer 策略
  useEffect(() => {
    if (!loadCategories) {
      setCategories([]);
      setCategoriesEnabled(false);
      return;
    }
    if (!authShopId) {
      setCategories([]);
      return;
    }
    const cached = peekShopCategories(authShopId);
    if (cached) {
      setCategories(cached);
      setCategoriesEnabled(true);
      return;
    }
    if (deferCategories) {
      setCategories([]);
      setCategoriesEnabled(false);
    } else {
      setCategoriesEnabled(true);
    }
  }, [authShopId, loadCategories, deferCategories]);

  useEffect(() => {
    if (!loadCategories || !categoriesEnabled || !authShopId) {
      if (!loadCategories) setCategoriesLoading(false);
      return undefined;
    }
    let cancelled = false;
    const cached = peekShopCategories(authShopId);
    if (cached) {
      setCategories(cached);
      setCategoriesLoading(false);
      // 仍走 load 以续期；有缓存则几乎同步返回
    } else {
      setCategoriesLoading(true);
    }
    loadShopCategoriesCached(authShopId, () => tiktokPlatformAPI.getShopCategories(authShopId))
      .then((data) => {
        if (!cancelled) setCategories(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled && !cached) setCategories([]);
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });
    return () => { cancelled = true; };
  }, [loadCategories, categoriesEnabled, authShopId]);

  // 列表出屏后 idle 预热类目，避免首次点筛选才等
  useEffect(() => {
    if (!loadCategories || !deferCategories || categoriesEnabled || !authShopId) {
      return undefined;
    }
    let idleId = 0;
    let timerId = 0;
    const run = () => setCategoriesEnabled(true);
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(run, { timeout: 2500 });
    } else {
      timerId = window.setTimeout(run, 1200);
    }
    return () => {
      if (idleId && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timerId) window.clearTimeout(timerId);
    };
  }, [loadCategories, deferCategories, categoriesEnabled, authShopId]);

  const listShopId = authShopId;
  const categoryShopId = authShopId;
  const canQuery = Boolean(authShopId);
  const isOfficialTab = isOfficialTemplateScope(scopeTab);
  const isPersonalTab = isPersonalTemplateScope(scopeTab);

  const categoryOptions = useMemo(
    () => flattenLeafCategories(categories).map((opt) => ({
      value: opt.categoryId,
      label: opt.label,
      productTypeId: opt.productTypeId,
    })),
    [categories],
  );

  const listQueryParams = (extra = {}) => ({
    scope: scopeTab,
    ...(listAllShops ? { allShops: true } : {}),
    ...extra,
  });

  const changeScopeTab = (tabId, { onReset } = {}) => {
    if (scopeTab === tabId) return;
    setScopeTab(tabId);
    onReset?.();
  };

  return {
    authShopId,
    scopeTab,
    setScopeTab,
    changeScopeTab,
    listShopId,
    categoryShopId,
    listAllShops,
    canQuery,
    isOfficialTab,
    isPersonalTab,
    canAccessTeam,
    categories,
    setCategories,
    categoryOptions,
    categoriesLoading,
    ensureCategoriesLoaded,
    listQueryParams,
    loadCategories: Boolean(loadCategories),
  };
}
